import React, { useState } from 'react';
import { Image, Card, Typography, Tag, Empty, Skeleton, Space, Row, Col, Input, Button, message } from 'antd';
import { PictureOutlined, FileTextOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { KnowledgeImage } from '../models';
import { mekakApi } from '../services';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ImageGalleryProps {
  images: KnowledgeImage[];
  loading?: boolean;
  editable?: boolean;
  onImageUpdated?: () => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, loading, editable = false, onImageUpdated }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());

  if (loading) {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2, 3].map((i) => (
          <Col key={i} xs={24} sm={12} md={8}>
            <Skeleton.Image active style={{ width: '100%', height: 200 }} />
          </Col>
        ))}
      </Row>
    );
  }

  if (!images || images.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No images" />;
  }

  const grouped = images.reduce<Record<number, KnowledgeImage[]>>((acc, img) => {
    const page = img.page_number || 0;
    if (!acc[page]) acc[page] = [];
    acc[page].push(img);
    return acc;
  }, {});

  const sortedPages = Object.keys(grouped).map(Number).sort((a, b) => a - b);

  const handleImageError = (imageId: string) => {
    setBrokenImages((prev) => new Set(prev).add(imageId));
  };

  const startEdit = (img: KnowledgeImage) => {
    setEditingId(img.id);
    setEditText(img.ocr_text || img.description || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async () => {
    if (!editingId || !editText.trim()) return;
    setSaving(true);
    try {
      const res = await mekakApi.updateImageDescription(editingId, editText.trim());
      if (res.data?.success) {
        message.success(res.data.message || '已更新');
        setEditingId(null);
        onImageUpdated?.();
      } else {
        message.error('更新失敗');
      }
    } catch {
      message.error('更新失敗');
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Image.PreviewGroup>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {sortedPages.map((page) => (
          <div key={page}>
            <Space style={{ marginBottom: 8 }}>
              <PictureOutlined />
              <Text strong>{page > 0 ? `Page ${page}` : 'Images'}</Text>
              <Tag>{grouped[page].length}</Tag>
            </Space>

            <Row gutter={[12, 12]}>
              {grouped[page].map((img) => (
                <Col key={img.id} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    size="small"
                    hoverable
                    style={{ borderRadius: 8, overflow: 'hidden' }}
                    cover={
                      brokenImages.has(img.id) ? (
                        <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                          <PictureOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />
                        </div>
                      ) : (
                        <Image
                          src={img.gcs_url}
                          alt={img.description || `Page ${img.page_number} image`}
                          style={{ objectFit: 'contain', maxHeight: 200 }}
                          onError={() => handleImageError(img.id)}
                          fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjZDlkOWQ5IiBmb250LXNpemU9IjE2Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4="
                        />
                      )
                    }
                  >
                    <div style={{ fontSize: 12 }}>
                      <Text type="secondary">
                        {img.width > 0 && `${img.width}x${img.height}`}
                        {img.file_size_bytes > 0 && ` | ${formatFileSize(img.file_size_bytes)}`}
                      </Text>
                    </div>

                    {/* Editing mode */}
                    {editingId === img.id ? (
                      <div style={{ marginTop: 8 }}>
                        <TextArea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={4}
                          style={{ fontSize: 12 }}
                          placeholder="描述這張圖片的內容..."
                        />
                        <Space style={{ marginTop: 6 }}>
                          <Button size="small" type="primary" icon={<CheckOutlined />} loading={saving} onClick={saveEdit}>
                            儲存
                          </Button>
                          <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit}>
                            取消
                          </Button>
                        </Space>
                      </div>
                    ) : (
                      <>
                        {/* Display OCR text */}
                        {img.ocr_text && (
                          <Paragraph
                            style={{ fontSize: 12, margin: '4px 0 0', color: '#595959' }}
                            ellipsis={{ rows: 3, expandable: true }}
                          >
                            <FileTextOutlined style={{ marginRight: 4 }} />
                            {img.ocr_text}
                          </Paragraph>
                        )}
                        {/* Edit button */}
                        {editable && (
                          <Button
                            size="small"
                            type="link"
                            icon={<EditOutlined />}
                            style={{ padding: 0, marginTop: 4, fontSize: 12 }}
                            onClick={() => startEdit(img)}
                          >
                            {img.ocr_text ? '編輯描述' : '新增描述'}
                          </Button>
                        )}
                      </>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ))}
      </div>
    </Image.PreviewGroup>
  );
};

export default ImageGallery;
